import { useState } from 'react';
import { Mail, Phone, MapPin, Send, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    product_type: '',
    monthly_volume: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const productTypes = [
    { value: 'tshirts', label: 'Custom T-Shirts' },
    { value: 'tote-bags', label: 'Tote Bags' },
    { value: 'posters', label: 'Posters & Wall Art' },
    { value: 'hats', label: 'Hats & Caps' },
  ];

  const volumeOptions = [
    { value: '1-50', label: '1-50 orders/month' },
    { value: '51-200', label: '51-200 orders/month' },
    { value: '201-500', label: '201-500 orders/month' },
    { value: '500+', label: '500+ orders/month' },
    { value: 'not-sure', label: "Not sure yet" },
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success('Thank you! We\'ll be in touch soon.');
      setFormData({
        name: '',
        email: '',
        company: '',
        phone: '',
        product_type: '',
        monthly_volume: '',
        message: '',
      });
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-24 md:py-32 bg-coastal-offwhite"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Left - Contact Info */}
          <div>
            <h2
              data-testid="contact-heading"
              className="font-heading text-3xl md:text-4xl lg:text-5xl tracking-tight font-bold text-coastal-navy mb-6"
            >
              Let's start{' '}
              <span className="text-coastal-orange">building together</span>
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed mb-10">
              Ready to launch your brand? Get in touch with our team to discuss
              your custom product needs and get a personalized quote.
            </p>

            {/* Contact Details */}
            <div
              data-testid="contact-details"
              className="space-y-6"
            >
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 bg-white rounded-xl flex items-center justify-center shadow-sm">
                  <Mail className="w-6 h-6 text-coastal-orange" />
                </div>
                <div>
                  <p className="font-heading font-semibold text-coastal-navy mb-1">
                    Email
                  </p>
                  <a
                    href="mailto:admin@CoastalRouteCommerce.shop"
                    className="text-gray-600 hover:text-coastal-orange transition-colors"
                  >
                    admin@CoastalRouteCommerce.shop
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 bg-white rounded-xl flex items-center justify-center shadow-sm">
                  <Phone className="w-6 h-6 text-coastal-orange" />
                </div>
                <div>
                  <p className="font-heading font-semibold text-coastal-navy mb-1">
                    Phone
                  </p>
                  <a
                    href="tel:+15154438615"
                    className="text-gray-600 hover:text-coastal-orange transition-colors"
                  >
                    +1 515 443 8615
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 bg-white rounded-xl flex items-center justify-center shadow-sm">
                  <MapPin className="w-6 h-6 text-coastal-orange" />
                </div>
                <div>
                  <p className="font-heading font-semibold text-coastal-navy mb-1">
                    Address
                  </p>
                  <p className="text-gray-600">
                    2015 E 43rd St, Tacoma WA 98404
                  </p>
                </div>
              </div>
            </div>

            {/* Business Hours */}
            <div className="mt-10 p-6 bg-white rounded-2xl border border-gray-200">
              <h3 className="font-heading font-semibold text-coastal-navy mb-3">
                Business Hours
              </h3>
              <div className="space-y-2 text-gray-600">
                <p className="flex justify-between">
                  <span>Monday - Friday</span>
                  <span className="font-medium">9:00 AM - 6:00 PM PST</span>
                </p>
                <p className="flex justify-between">
                  <span>Saturday - Sunday</span>
                  <span className="font-medium">Closed</span>
                </p>
              </div>
            </div>
          </div>

          {/* Right - Form */}
          <div
            data-testid="contact-form-wrapper"
            className="bg-white rounded-3xl p-8 md:p-10 border border-gray-200 shadow-lg"
          >
            <h3 className="font-heading font-bold text-2xl text-coastal-navy mb-6">
              Request a Quote
            </h3>

            <form
              data-testid="contact-form"
              onSubmit={handleSubmit}
              className="space-y-5"
            >
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Full Name *
                  </label>
                  <Input
                    data-testid="input-name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="John Doe"
                    required
                    className="rounded-xl"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email *
                  </label>
                  <Input
                    data-testid="input-email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="john@example.com"
                    required
                    className="rounded-xl"
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Company
                  </label>
                  <Input
                    data-testid="input-company"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    placeholder="Your Brand Name"
                    className="rounded-xl"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone
                  </label>
                  <Input
                    data-testid="input-phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+1 (555) 000-0000"
                    className="rounded-xl"
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Product Type *
                  </label>
                  <Select
                    value={formData.product_type}
                    onValueChange={(value) =>
                      handleSelectChange('product_type', value)
                    }
                    required
                  >
                    <SelectTrigger
                      data-testid="select-product-type"
                      className="rounded-xl"
                    >
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {productTypes.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Monthly Volume *
                  </label>
                  <Select
                    value={formData.monthly_volume}
                    onValueChange={(value) =>
                      handleSelectChange('monthly_volume', value)
                    }
                    required
                  >
                    <SelectTrigger
                      data-testid="select-volume"
                      className="rounded-xl"
                    >
                      <SelectValue placeholder="Expected volume" />
                    </SelectTrigger>
                    <SelectContent>
                      {volumeOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tell us about your project *
                </label>
                <Textarea
                  data-testid="input-message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Describe your brand, products, and what you're looking to achieve..."
                  rows={4}
                  required
                  className="rounded-xl resize-none"
                />
              </div>

              <Button
                data-testid="submit-btn"
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-coastal-orange hover:bg-coastal-orange/90 text-white rounded-full py-6 text-lg font-semibold transition-all hover:-translate-y-0.5 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5 mr-2" />
                    Send Request
                  </>
                )}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;
